// Drawing App - Tool Definitions

import { Point, BrushSettings, ToolType, ShapeType, Layer } from './types';
import { BrushEngine } from './brush_engine';
import { Stabilizer } from './stabilizer';
import { LayerManager } from './layer_manager';
import { CanvasEngine } from './canvas_engine';
import { HistoryManager } from './history_manager';
import { SymmetryEngine } from './symmetry';
import { ShapeDetector } from './shape_detector';
import { TransformManager } from './transform';

export interface Tool {
  type: ToolType;
  onPointerDown(point: Point, ctx: ToolContext): void;
  onPointerMove(point: Point, ctx: ToolContext): void;
  onPointerUp(point: Point, ctx: ToolContext): void;
}

export interface ToolContext {
  layerManager: LayerManager;
  canvasEngine: CanvasEngine;
  historyManager: HistoryManager;
  brushEngine: BrushEngine;
  stabilizer: Stabilizer;
  symmetryEngine: SymmetryEngine;
  shapeDetector: ShapeDetector;
  transformManager: TransformManager;
  brushSettings: BrushSettings;
  primaryColor: string;
  shapeType: ShapeType;
  onColorPicked?: (color: string) => void;
}

export class BrushTool implements Tool {
  type: ToolType = 'brush';
  private isDrawing = false;
  private layerSnapshot: ImageData | null = null;

  onPointerDown(point: Point, ctx: ToolContext): void {
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer || layer.locked) return;

    this.isDrawing = true;
    const { width, height } = ctx.canvasEngine.getCanvasSize();
    this.layerSnapshot = ctx.layerManager.getLayerSnapshot(layer.id);

    ctx.brushEngine.beginStroke(width, height);
    const smoothed = ctx.stabilizer.process(point);
    const symPoints = ctx.symmetryEngine.transform(smoothed, width, height);
    for (const sp of symPoints) {
      ctx.brushEngine.addPoint(sp, ctx.brushSettings, ctx.primaryColor);
    }

    // Show active stroke in real-time
    ctx.canvasEngine.setActiveStroke(ctx.brushEngine.getStrokeCanvas(), ctx.brushSettings.opacity, layer.id);
    ctx.canvasEngine.hideBrushCursor();
  }

  onPointerMove(point: Point, ctx: ToolContext): void {
    if (!this.isDrawing) return;
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer) return;

    const smoothed = ctx.stabilizer.process(point);
    const { width, height } = ctx.canvasEngine.getCanvasSize();
    const symPoints = ctx.symmetryEngine.transform(smoothed, width, height);
    for (const sp of symPoints) {
      ctx.brushEngine.addPoint(sp, ctx.brushSettings, ctx.primaryColor);
    }

    // Update active stroke display
    ctx.canvasEngine.setActiveStroke(ctx.brushEngine.getStrokeCanvas(), ctx.brushSettings.opacity, layer.id);
  }

  onPointerUp(point: Point, ctx: ToolContext): void {
    if (!this.isDrawing) return;
    this.isDrawing = false;

    const layer = ctx.layerManager.getActiveLayer();
    if (!layer) return;

    // Clear active stroke overlay before compositing
    ctx.canvasEngine.setActiveStroke(null);

    const layerCtx = layer.canvas.getContext('2d')!;
    ctx.brushEngine.endStroke(layerCtx, ctx.brushSettings.opacity, 'source-over');
    ctx.stabilizer.reset();

    // Save to history
    if (this.layerSnapshot) {
      const snapshot = this.layerSnapshot;
      const layerId = layer.id;
      const newSnapshot = ctx.layerManager.getLayerSnapshot(layerId)!;
      ctx.historyManager.push({
        type: 'stroke',
        undo: () => ctx.layerManager.restoreLayerSnapshot(layerId, snapshot),
        redo: () => ctx.layerManager.restoreLayerSnapshot(layerId, newSnapshot)
      });
      this.layerSnapshot = null;
    }

    ctx.canvasEngine.requestRender();
  }
}

export class EraserTool implements Tool {
  type: ToolType = 'eraser';
  private isDrawing = false;
  private lastPoint: Point | null = null;
  private layerSnapshot: ImageData | null = null;

  onPointerDown(point: Point, ctx: ToolContext): void {
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer || layer.locked) return;

    this.isDrawing = true;
    this.lastPoint = point;
    this.layerSnapshot = ctx.layerManager.getLayerSnapshot(layer.id);

    const layerCtx = layer.canvas.getContext('2d')!;
    ctx.brushEngine.renderEraserDab(layerCtx, point.x, point.y, ctx.brushSettings.size, point.pressure, ctx.brushSettings.hardness);
    ctx.canvasEngine.requestRender();
  }

  onPointerMove(point: Point, ctx: ToolContext): void {
    if (!this.isDrawing) return;
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer) return;

    const layerCtx = layer.canvas.getContext('2d')!;
    const smoothed = ctx.stabilizer.process(point);

    if (this.lastPoint) {
      const dx = smoothed.x - this.lastPoint.x;
      const dy = smoothed.y - this.lastPoint.y;
      const dist = Math.hypot(dx, dy);
      const spacing = Math.max(1, ctx.brushSettings.size * ctx.brushSettings.spacing);
      const steps = Math.ceil(dist / spacing);

      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const x = this.lastPoint.x + dx * t;
        const y = this.lastPoint.y + dy * t;
        ctx.brushEngine.renderEraserDab(layerCtx, x, y, ctx.brushSettings.size, smoothed.pressure, ctx.brushSettings.hardness);
      }
    }

    this.lastPoint = smoothed;
    ctx.canvasEngine.requestRender();
  }

  onPointerUp(point: Point, ctx: ToolContext): void {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.lastPoint = null;
    ctx.stabilizer.reset();

    const layer = ctx.layerManager.getActiveLayer();
    if (!layer || !this.layerSnapshot) return;

    const snapshot = this.layerSnapshot;
    const layerId = layer.id;
    const newSnapshot = ctx.layerManager.getLayerSnapshot(layerId)!;
    ctx.historyManager.push({
      type: 'erase',
      undo: () => ctx.layerManager.restoreLayerSnapshot(layerId, snapshot),
      redo: () => ctx.layerManager.restoreLayerSnapshot(layerId, newSnapshot)
    });
    this.layerSnapshot = null;
  }
}

export class EyedropperTool implements Tool {
  type: ToolType = 'eyedropper';

  onPointerDown(point: Point, ctx: ToolContext): void {
    this.pickColor(point, ctx);
  }

  onPointerMove(point: Point, ctx: ToolContext): void {
    this.pickColor(point, ctx);
  }

  onPointerUp(_point: Point, _ctx: ToolContext): void {}

  private pickColor(point: Point, ctx: ToolContext): void {
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer) return;

    const layerCtx = layer.canvas.getContext('2d')!;
    const pixel = layerCtx.getImageData(Math.floor(point.x), Math.floor(point.y), 1, 1).data;
    const hex = '#' + [pixel[0], pixel[1], pixel[2]].map(v => v.toString(16).padStart(2, '0')).join('');

    if (ctx.onColorPicked) {
      ctx.onColorPicked(hex);
    }
  }
}

export class FillTool implements Tool {
  type: ToolType = 'fill';

  onPointerDown(point: Point, ctx: ToolContext): void {
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer || layer.locked) return;

    const { width, height } = ctx.canvasEngine.getCanvasSize();
    const layerCtx = layer.canvas.getContext('2d')!;
    const snapshot = ctx.layerManager.getLayerSnapshot(layer.id)!;

    this.floodFill(layerCtx, Math.floor(point.x), Math.floor(point.y), ctx.primaryColor, width, height);

    const layerId = layer.id;
    const newSnapshot = ctx.layerManager.getLayerSnapshot(layerId)!;
    ctx.historyManager.push({
      type: 'fill',
      undo: () => ctx.layerManager.restoreLayerSnapshot(layerId, snapshot),
      redo: () => ctx.layerManager.restoreLayerSnapshot(layerId, newSnapshot)
    });

    ctx.canvasEngine.requestRender();
  }

  onPointerMove(_point: Point, _ctx: ToolContext): void {}
  onPointerUp(_point: Point, _ctx: ToolContext): void {}

  private floodFill(ctx: OffscreenCanvasRenderingContext2D, startX: number, startY: number, fillColor: string, width: number, height: number): void {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const targetIdx = (startY * width + startX) * 4;
    const targetR = data[targetIdx];
    const targetG = data[targetIdx + 1];
    const targetB = data[targetIdx + 2];
    const targetA = data[targetIdx + 3];

    // Parse fill color
    const r = parseInt(fillColor.slice(1, 3), 16);
    const g = parseInt(fillColor.slice(3, 5), 16);
    const b = parseInt(fillColor.slice(5, 7), 16);

    // Don't fill if same color
    if (targetR === r && targetG === g && targetB === b && targetA === 255) return;

    const tolerance = 32;
    const stack: [number, number][] = [[startX, startY]];
    const visited = new Uint8Array(width * height);

    while (stack.length > 0) {
      const [x, y] = stack.pop()!;
      if (x < 0 || x >= width || y < 0 || y >= height) continue;

      const idx = y * width + x;
      if (visited[idx]) continue;

      const pixIdx = idx * 4;
      const dr = Math.abs(data[pixIdx] - targetR);
      const dg = Math.abs(data[pixIdx + 1] - targetG);
      const db = Math.abs(data[pixIdx + 2] - targetB);
      const da = Math.abs(data[pixIdx + 3] - targetA);

      if (dr + dg + db + da > tolerance) continue;

      visited[idx] = 1;
      data[pixIdx] = r;
      data[pixIdx + 1] = g;
      data[pixIdx + 2] = b;
      data[pixIdx + 3] = 255;

      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }

    ctx.putImageData(imageData, 0, 0);
  }
}

export class ShapeTool implements Tool {
  type: ToolType = 'shape';
  private startPoint: Point | null = null;
  private layerSnapshot: ImageData | null = null;

  onPointerDown(point: Point, ctx: ToolContext): void {
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer || layer.locked) return;

    this.startPoint = point;
    this.layerSnapshot = ctx.layerManager.getLayerSnapshot(layer.id);
  }

  onPointerMove(point: Point, ctx: ToolContext): void {
    if (!this.startPoint || !this.layerSnapshot) return;
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer) return;

    // Restore snapshot and draw preview
    ctx.layerManager.restoreLayerSnapshot(layer.id, this.layerSnapshot);
    const layerCtx = layer.canvas.getContext('2d')!;
    this.drawShape(layerCtx, this.startPoint, point, ctx);
    ctx.canvasEngine.requestRender();
  }

  onPointerUp(point: Point, ctx: ToolContext): void {
    if (!this.startPoint) return;
    const layer = ctx.layerManager.getActiveLayer();
    if (!layer || !this.layerSnapshot) return;

    // Check if shape detection should override
    ctx.layerManager.restoreLayerSnapshot(layer.id, this.layerSnapshot);
    const layerCtx = layer.canvas.getContext('2d')!;
    this.drawShape(layerCtx, this.startPoint, point, ctx);

    const snapshot = this.layerSnapshot;
    const layerId = layer.id;
    const newSnapshot = ctx.layerManager.getLayerSnapshot(layerId)!;
    ctx.historyManager.push({
      type: 'shape',
      undo: () => ctx.layerManager.restoreLayerSnapshot(layerId, snapshot),
      redo: () => ctx.layerManager.restoreLayerSnapshot(layerId, newSnapshot)
    });

    this.startPoint = null;
    this.layerSnapshot = null;
    ctx.canvasEngine.requestRender();
  }

  private drawShape(ctx: OffscreenCanvasRenderingContext2D, start: Point, end: Point, toolCtx: ToolContext): void {
    ctx.save();
    ctx.strokeStyle = toolCtx.primaryColor;
    ctx.lineWidth = toolCtx.brushSettings.size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = toolCtx.brushSettings.opacity;

    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);

    ctx.beginPath();
    switch (toolCtx.shapeType) {
      case 'line':
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        break;
      case 'rectangle':
        ctx.rect(x, y, w, h);
        break;
      case 'ellipse':
        ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
        break;
      case 'triangle':
        ctx.moveTo(x + w / 2, y);
        ctx.lineTo(x + w, y + h);
        ctx.lineTo(x, y + h);
        ctx.closePath();
        break;
    }
    ctx.stroke();
    ctx.restore();
  }
}

export class PanTool implements Tool {
  type: ToolType = 'pan';

  onPointerDown(point: Point, ctx: ToolContext): void {
    const screenPos = ctx.transformManager.canvasToScreen(point.x, point.y);
    ctx.transformManager.startPan(screenPos.x, screenPos.y);
  }

  onPointerMove(point: Point, ctx: ToolContext): void {
    const screenPos = ctx.transformManager.canvasToScreen(point.x, point.y);
    ctx.transformManager.movePan(screenPos.x, screenPos.y);
  }

  onPointerUp(_point: Point, ctx: ToolContext): void {
    ctx.transformManager.endPan();
  }
}

export function createTool(type: ToolType): Tool {
  switch (type) {
    case 'brush': return new BrushTool();
    case 'eraser': return new EraserTool();
    case 'eyedropper': return new EyedropperTool();
    case 'fill': return new FillTool();
    case 'shape': return new ShapeTool();
    case 'pan': return new PanTool();
    default: return new BrushTool();
  }
}
